import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import PlatformIcon from './common/PlatformIcon';
import { PINKE_BRAND_COLORS } from '../../../core/src/theme/brandColors';

export type ModuleType =
  | 'travel'
  | 'carpool'
  | 'dining'
  | 'shopping'
  | 'toolbox'
  | 'rental'
  | 'ledger'
  | 'todo'
  | 'market'
  | 'discovery'
  | 'health'
  | 'grouporder'
  | 'dashboard';

export type ModuleHeaderButtonStyle = 'default' | 'save' | 'create' | 'cancel';

export interface ModuleHeaderRightButton {
  icon: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  style?: ModuleHeaderButtonStyle;
}

export interface ModuleHeaderProps {
  moduleType: ModuleType;
  title: string;
  subtitle?: string;
  showMiniCube?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
  showCreateButton?: boolean;
  onCreatePress?: () => void;
  /**
   * When true, render header with transparent background and no shadow/border
   * so content behind (e.g., a hero image) can show through.
   */
  transparent?: boolean;
  rightButton?: ModuleHeaderRightButton;
  rightButtons?: ModuleHeaderRightButton[];
  topInset?: number;
  /**
   * When true, remove top/left/right padding so the header can span edge-to-edge (useful for web pages)
   */
  noEdgePadding?: boolean;
}

const iconMap: Record<ModuleType, string> = {
  travel: 'flight',
  carpool: 'directions-car',
  dining: 'restaurant',
  shopping: 'shopping-bag',
  toolbox: 'build',
  rental: 'home',
  ledger: 'account-balance-wallet',
  todo: 'check-circle',
  market: 'store',
  discovery: 'visibility',
  health: 'fitness-center',
  grouporder: 'delivery-dining',
  dashboard: 'apps',
};

const colorMap: Record<ModuleType, string> = {
  travel: PINKE_BRAND_COLORS[0],
  carpool: PINKE_BRAND_COLORS[1],
  dining: PINKE_BRAND_COLORS[2],
  shopping: PINKE_BRAND_COLORS[3],
  toolbox: PINKE_BRAND_COLORS[4],
  rental: PINKE_BRAND_COLORS[5],
  ledger: PINKE_BRAND_COLORS[6],
  todo: PINKE_BRAND_COLORS[7],
  market: PINKE_BRAND_COLORS[8],
  discovery: PINKE_BRAND_COLORS[1],
  health: PINKE_BRAND_COLORS[8],
  grouporder: PINKE_BRAND_COLORS[5],
  dashboard: PINKE_BRAND_COLORS[0],
};

const moduleIndexMap: Record<ModuleType, number> = {
  travel: 0,
  carpool: 1,
  dining: 2,
  shopping: 3,
  toolbox: 4,
  rental: 5,
  ledger: 6,
  todo: 7,
  market: 8,
  discovery: 9,
  health: 8,
  grouporder: 5,
  dashboard: -1,
};

// canonical header height used across the app
export const HEADER_MIN_HEIGHT = 72;

const ModuleHeader: React.FC<ModuleHeaderProps> = ({
  moduleType,
  title,
  subtitle,
  showMiniCube = true,
  showBackButton = false,
  onBack,
  showCloseButton = false,
  onClose,
  showCreateButton = false,
  onCreatePress,
  onMenuPress,
  rightButton,
  rightButtons,
  topInset = 0,
  noEdgePadding = false,
  transparent = false,
}) => {
  const backgroundColor = transparent ? 'transparent' : (colorMap[moduleType] || PINKE_BRAND_COLORS[0]);
  const icon = iconMap[moduleType] || 'apps';
  const currentModuleIndex = moduleIndexMap[moduleType] ?? 0;

  const handleCubePress = () => {
    // Menu press takes precedence if provided (hamburger behavior)
    // DEBUG: log to help locate whether this runtime file is used
    try {
      // eslint-disable-next-line no-console
      console.log('[ModuleHeader] cube pressed, onMenuPress?', Boolean(onMenuPress));
    } catch (e) {}
    if (onMenuPress) {
      onMenuPress();
      return;
    }
    if (showBackButton && onBack) {
      onBack();
    }
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor,
          // always keep vertical padding to maintain consistent header height across pages
          paddingTop: topInset + 8,
          paddingBottom: 12,
          paddingHorizontal: noEdgePadding ? 0 : 16,
          minHeight: HEADER_MIN_HEIGHT,
        },
        // if transparent, remove border/shadow overrides
        transparent && Platform.select({ web: { borderBottomWidth: 0, boxShadow: 'none' }, default: { borderBottomWidth: 0, shadowOpacity: 0, elevation: 0 } }),
      ]}
    >
      {showMiniCube ? (
        // when noEdgePadding is true, still provide a small left inset for the cube
        <View style={[styles.leftContainer, noEdgePadding ? { paddingLeft: 12 } : undefined]}>
          <TouchableOpacity
            activeOpacity={showBackButton ? 0.7 : 1}
            onPress={handleCubePress}
            disabled={!showBackButton}
          >
            <View style={styles.miniCube}>
              {PINKE_BRAND_COLORS.slice(0, 9).map((color, index) => (
                <View key={color + index} style={[styles.cubeItem, { backgroundColor: color }]}>
                  {index === currentModuleIndex && currentModuleIndex !== -1 ? (
                    <View style={styles.cubeIcon}>
                      <PlatformIcon name={icon} size={10} color="#fff" family="MaterialIcons" />
                    </View>
                  ) : null}
                  {showBackButton && index === (moduleType === 'grouporder' ? 3 : 5) ? (
                    <View style={styles.backIcon}>
                      <PlatformIcon name="keyboard-arrow-left" size={12} color="#fff" family="MaterialIcons" />
                    </View>
                  ) : null}
                </View>
              ))}
            </View>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.leftContainer} />
      )}

      <View style={styles.centerTextContainer}>
        <Text style={styles.title}>{title}</Text>
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      </View>

      <View style={[styles.rightContainer, noEdgePadding ? { paddingRight: 12 } : undefined]}>
        {rightButtons?.map((button, index) => (
          <TouchableOpacity
            key={`${button.icon}-${index}`}
            style={[
              styles.rightActionButton,
              button.style === 'cancel' && styles.cancelActionButton,
              button.style === 'save' && [styles.saveActionButton, { backgroundColor }],
              button.disabled && styles.disabledActionButton,
            ]}
            onPress={button.onPress}
            activeOpacity={0.7}
            disabled={button.disabled || button.loading}
          >
            <PlatformIcon
              name={button.loading ? 'refresh' : button.icon}
              size={18}
              color={button.style === 'save' ? '#fff' : button.style === 'cancel' ? '#666' : '#fff'}
              family="MaterialIcons"
            />
          </TouchableOpacity>
        ))}

        {rightButton && !rightButtons ? (
          <TouchableOpacity
            style={
              rightButton.style === 'create' || rightButton.style === 'save'
                ? [styles.createButton, { backgroundColor, marginRight: 8 }]
                : [styles.rightActionButton, { backgroundColor: 'rgba(255,255,255,0.2)' }]
            }
            onPress={rightButton.onPress}
            activeOpacity={0.7}
            disabled={rightButton.disabled}
          >
            <PlatformIcon
              name={rightButton.icon}
              size={rightButton.style === 'create' || rightButton.style === 'save' ? 24 : 20}
              color="#fff"
              family="MaterialIcons"
            />
          </TouchableOpacity>
        ) : null}

        {showCloseButton && !rightButtons ? (
          <TouchableOpacity
            style={[styles.closeButton, { backgroundColor: 'rgba(255,255,255,0.2)' }]}
            onPress={onClose}
            activeOpacity={0.7}
          >
            <PlatformIcon name="close" size={20} color="#fff" family="MaterialIcons" />
          </TouchableOpacity>
        ) : null}

        {showCreateButton && !rightButtons ? (
          <TouchableOpacity
            style={[styles.createButton, { backgroundColor }]}
            onPress={onCreatePress}
            activeOpacity={0.7}
          >
            <PlatformIcon name="add" size={24} color="#fff" family="MaterialIcons" />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: Platform.select({
    web: {
      position: 'relative', // Explicitly set to relative (not fixed) to avoid browser chrome overlap
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingBottom: 12,
      zIndex: 10,
    },
    default: {
      position: 'relative', // Explicitly set to relative (not fixed) to avoid browser chrome overlap
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingBottom: 12,
    },
  }),
  leftContainer: {
    width: 40,
    alignItems: 'flex-start',
  },
  miniCube: Platform.select({
    web: {
      width: 36,
      height: 36,
      flexDirection: 'row',
      flexWrap: 'wrap',
      borderRadius: 8,
      overflow: 'hidden',
      marginRight: 12,
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
    },
    default: {
      width: 36,
      height: 36,
      flexDirection: 'row',
      flexWrap: 'wrap',
      borderRadius: 8,
      overflow: 'hidden',
      marginRight: 12,
      justifyContent: 'center',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 6,
    }
  }),
  cubeItem: {
    width: 12,
    height: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cubeIcon: {
    textAlign: 'center',
  },
  backIcon: {
    position: 'absolute',
    textAlign: 'center',
  },
  centerTextContainer: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#fff',
    textAlign: 'center',
    marginTop: 2,
    opacity: 0.9,
    fontWeight: '400',
  },
  rightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: 40,
  },
  createButton: Platform.select({
    web: {
      width: 36,
      height: 36,
      borderRadius: 18,
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
    },
    default: {
      width: 36,
      height: 36,
      borderRadius: 18,
      justifyContent: 'center',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.5,
      shadowRadius: 6,
      elevation: 10,
    }
  }),
  rightActionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  cancelActionButton: {
    backgroundColor: 'rgba(255,255,255,0.9)',
  },
  saveActionButton: {
    backgroundColor: '#4CAF50',
  },
  disabledActionButton: {
    backgroundColor: 'rgba(255,255,255,0.3)',
    opacity: 0.6,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.12)',
    marginRight: 8,
  },
});

export default ModuleHeader;
